/**
 * WizardStepDocuments - Step de anexação de documentos no wizard de contrato
 * Exibe tipos de documento exigidos pelo contrato publicado
 * Permite upload de novos ou reutilização de existentes do cliente
 * Abre preview interno via ImageLightbox ao invés de nova aba
 * 
 * @version 3.0.0
 * RELEASE: 2026-04-09 v3.0.0 - Preview interno via ImageLightbox
 */
import { useState, useRef } from 'react';
import {
  FileCheck,
  Upload,
  Check,
  AlertCircle,
  Loader2,
  Eye,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import { ImageLightbox, type MediaItem } from '@/components/tenant/chat/ImageLightbox';
import { getMimeMediaType } from '@/lib/file-utils';
import type { RequiredDocument } from '@/hooks/usePublishedContractRequiredDocs';
import type { CustomerDocument } from '@/hooks/useCustomerDocuments';

interface WizardStepDocumentsProps {
  /** Documentos exigidos pelo contrato publicado */
  requiredDocs: RequiredDocument[];
  /** Documentos já existentes do cliente */
  customerDocs: CustomerDocument[];
  /** Callback para upload de arquivo */
  onUpload: (file: File, documentTypeId: string) => Promise<void>;
  /** Callback para obter URL pública do documento */
  onGetPublicUrl: (storagePath: string) => string;
  /** Indica se há upload em andamento */
  isUploading: boolean;
}

/**
 * Step de documentos no wizard de geração de contrato
 */
export function WizardStepDocuments({
  requiredDocs,
  customerDocs,
  onUpload,
  onGetPublicUrl,
  isUploading,
}: WizardStepDocumentsProps) {
  const [uploadingTypeId, setUploadingTypeId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [activeTypeId, setActiveTypeId] = useState<string | null>(null);

  // Estado do lightbox para preview interno
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxItems, setLightboxItems] = useState<MediaItem[]>([]);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Verifica se um tipo de documento já tem arquivo do cliente
  const getExistingDoc = (typeId: string): CustomerDocument | undefined => {
    return customerDocs.find((d) => d.document_type_id === typeId);
  };

  // Handler para iniciar upload
  const handleUploadClick = (typeId: string) => {
    setActiveTypeId(typeId);
    fileInputRef.current?.click();
  };

  // Handler do input file
  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !activeTypeId) return;

    setUploadingTypeId(activeTypeId);
    try {
      await onUpload(file, activeTypeId);
    } catch {
      toast.error('Erro ao enviar documento');
    } finally {
      setUploadingTypeId(null);
      setActiveTypeId(null);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  /**
   * Abre preview interno via ImageLightbox
   * Para imagens/vídeos: abre no lightbox
   * Para PDFs/docs: fallback abrindo em nova aba
   */
  /**
   * Abre preview interno via ImageLightbox para todos os tipos
   * Imagens/vídeos: renderizados nativamente; PDFs: via iframe
   */
  const handleView = (storagePath: string, mimeType?: string | null, fileName?: string) => {
    const url = onGetPublicUrl(storagePath);
    if (!url) return;

    const mediaType = getMimeMediaType(mimeType) as 'image' | 'video' | 'document';
    setLightboxItems([{ url, type: mediaType, title: fileName }]);
    setLightboxIndex(0);
    setLightboxOpen(true);
  };

  if (requiredDocs.length === 0) {
    return (
      <div className="py-12 text-center">
        <FileCheck className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
        <p className="text-sm text-muted-foreground">
          Este contrato não exige documentos anexados.
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Você pode avançar para a próxima etapa.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Input file oculto */}
      <input
        ref={fileInputRef}
        type="file"
        className="hidden"
        onChange={handleFileChange}
        accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx"
      />

      <p className="text-sm text-muted-foreground">
        Anexe os documentos exigidos abaixo. Documentos já cadastrados no cliente
        serão reutilizados automaticamente.
      </p>

      <div className="space-y-2">
        {requiredDocs.map((req) => {
          const existing = getExistingDoc(req.document_type_id);
          const isUploading_ = uploadingTypeId === req.document_type_id;
          const typeName = req.document_types?.name || 'Documento';

          return (
            <div
              key={req.id}
              className={cn(
                'flex items-center justify-between gap-3 px-4 py-3 border rounded-lg transition-colors',
                existing ? 'border-chart-2/50 bg-chart-2/5' : req.is_required ? 'border-destructive/30' : 'border-border'
              )}
            >
              {/* Info do documento */}
              <div className="flex items-center gap-3 min-w-0">
                <div className={cn(
                  'h-8 w-8 rounded-lg flex items-center justify-center flex-shrink-0',
                  existing ? 'bg-chart-2/10' : 'bg-muted'
                )}>
                  {existing ? (
                    <Check className="h-4 w-4 text-chart-2" />
                  ) : (
                    <FileCheck className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>

                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium truncate">{typeName}</p>
                    {req.is_required ? (
                      <Badge variant="destructive" className="text-[10px] px-1.5 py-0">
                        Obrigatório
                      </Badge>
                    ) : (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                        Opcional
                      </Badge>
                    )}
                  </div>
                  {existing && (
                    <p className="text-xs text-muted-foreground truncate">
                      {existing.file_name}
                    </p>
                  )}
                  {!existing && req.document_types?.description && (
                    <p className="text-xs text-muted-foreground truncate">
                      {req.document_types.description}
                    </p>
                  )}
                </div>
              </div>

              {/* Ações */}
              <div className="flex items-center gap-2 flex-shrink-0">
                {existing ? (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => handleView(existing.storage_path, existing.mime_type, existing.file_name)}
                    >
                      <Eye className="h-3.5 w-3.5 mr-1" />
                      Ver
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="h-7 text-xs"
                      onClick={() => handleUploadClick(req.document_type_id)}
                      disabled={isUploading}
                    >
                      <Upload className="h-3.5 w-3.5 mr-1" />
                      Substituir
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => handleUploadClick(req.document_type_id)}
                    disabled={isUploading}
                  >
                    {isUploading_ ? (
                      <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                    ) : (
                      <Upload className="h-3.5 w-3.5 mr-1" />
                    )}
                    Enviar
                  </Button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Aviso sobre documentos obrigatórios faltantes */}
      {requiredDocs.some((r) => r.is_required && !getExistingDoc(r.document_type_id)) && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-destructive/5 border border-destructive/20">
          <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
          <p className="text-xs text-destructive">
            Existem documentos obrigatórios pendentes. Anexe-os para poder avançar.
          </p>
        </div>
      )}

      {/* Lightbox para preview interno de imagens e vídeos */}
      <ImageLightbox
        open={lightboxOpen}
        onClose={() => setLightboxOpen(false)}
        mediaItems={lightboxItems}
        initialIndex={lightboxIndex}
      />
    </div>
  );
}
